import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sunrise, Send, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { InfoTip } from './InfoTip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 12 UTC = 07:00 en Ecuador (UTC-5)
const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${String((h - 5 + 24) % 24).padStart(2, '0')}:00 (tu hora) · ${String(h).padStart(2, '0')}:00 UTC`,
}));

export const MorningDigestCard = () => {
  const [cfg, setCfg] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ enabled: true, whatsapp_number: '', campaign_id: '', hour_utc: 12 });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([
        axios.get(`${API}/noah/morning-digest`),
        axios.get(`${API}/whatsapp-cloud/accounts`),
      ]);
      setCfg(d.data);
      setCampaigns(c.data?.accounts || []);
      setForm({
        enabled: d.data.enabled ?? true,
        whatsapp_number: d.data.whatsapp_number || '',
        campaign_id: d.data.campaign_id || c.data?.accounts?.[0]?.campaign_id || '',
        hour_utc: d.data.hour_utc ?? 12,
      });
    } catch {
      /* silencioso */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.whatsapp_number || !form.campaign_id) {
      toast.error('Necesito tu número de WhatsApp y una campaña con WhatsApp Cloud conectado');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/noah/morning-digest`, form);
      toast.success('Resumen matutino programado');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/noah/morning-digest/send`);
      data.status === 'sent'
        ? toast.success(`Enviado a WhatsApp (audio: ${data.audio_sent ? 'sí' : 'no'})`)
        : toast.error('No se pudo enviar — revisa el WhatsApp de la campaña');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar');
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    await axios.delete(`${API}/noah/morning-digest`);
    toast.info('Resumen matutino desactivado');
    await load();
  };

  return (
    <Card className="bg-card/80 backdrop-blur border-white/10" data-testid="morning-digest-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Sunrise className="w-4 h-4 text-brand-copper" />
          <h3 className="font-semibold text-foreground">Resumen matutino por WhatsApp</h3>
          <InfoTip
            title="Resumen matutino hablado"
            text="Cada mañana Noah calcula tu resumen del día, lo graba con su voz y te lo manda por WhatsApp (texto + nota de voz)."
            source="Métricas del día + voz de Noah (OpenAI TTS) enviadas con el WhatsApp Cloud de la campaña elegida"
            to="/campaigns"
            linkLabel="Conectar WhatsApp a una campaña"
          />
          {cfg?.configured && (
            <Badge className="ml-auto bg-brand-green/15 text-brand-green border-brand-green/30">
              {cfg.enabled ? 'Activo' : 'Pausado'}
            </Badge>
          )}
        </div>

        {campaigns.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Primero conecta WhatsApp Cloud en una campaña (botón <strong>WhatsApp</strong> en Campañas) y aquí podrás activar el envío.
          </p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Tu WhatsApp (E.164)</Label>
                <Input data-testid="digest-number" placeholder="+593999999999"
                  value={form.whatsapp_number}
                  onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Campaña que envía</Label>
                <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
                  <SelectTrigger data-testid="digest-campaign"><SelectValue placeholder="Elige campaña" /></SelectTrigger>
                  <SelectContent>
                    {campaigns.map((c) => (
                      <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.campaign_name || c.campaign_id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Hora de envío</Label>
                <Select value={String(form.hour_utc)} onValueChange={(v) => setForm({ ...form, hour_utc: parseInt(v, 10) })}>
                  <SelectTrigger data-testid="digest-hour"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {HOURS.map((h) => <SelectItem key={h.value} value={String(h.value)}>{h.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-5">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="digest-enabled" />
                <span className="text-sm text-muted-foreground">Enviar cada mañana</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={save} disabled={saving} data-testid="digest-save" className="gap-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sunrise className="w-4 h-4" />} Guardar
              </Button>
              {cfg?.configured && (
                <>
                  <Button variant="outline" onClick={sendNow} disabled={sending} data-testid="digest-send-now" className="gap-1">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviármelo ahora
                  </Button>
                  <Button variant="ghost" onClick={remove} data-testid="digest-delete" className="text-destructive gap-1">
                    <Trash2 className="w-4 h-4" /> Quitar
                  </Button>
                </>
              )}
            </div>

            {cfg?.last_sent_at && (
              <p className="text-[11px] text-muted-foreground">
                Último envío: {new Date(cfg.last_sent_at).toLocaleString('es')} · {cfg.last_status}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MorningDigestCard;
